import React, { ComponentType, FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import {
  HumanizerErc7730Visualization,
  HumanizerVisualization
} from '@ambire-common/libs/humanizer/interfaces'
import Text from '@common/components/Text'
import { isKnownErc7730FieldLabel } from '@common/config/localization/humanizerMessages'
import { I18N_NAMESPACE } from '@common/config/localization/i18nCore'
import { SPACING_MI, SPACING_TY } from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { translateErc7730FieldLabel } from '@common/utils/humanizerTemplate'

// The props of HumanizedVisualization this component renders its parts with. Taken as a prop
// instead of imported, because HumanizedVisualization renders this component.
type PartsRendererProps = {
  data: HumanizerVisualization[]
  sizeMultiplierSize: number
  textSize: number
  chainId: bigint
  isHistory?: boolean
  imageSize: number
  hideLinks: boolean
  hasPadding: boolean
}

interface Props extends Omit<PartsRendererProps, 'data' | 'hasPadding'> {
  visualization: HumanizerErc7730Visualization
  PartsRenderer: ComponentType<PartsRendererProps>
}

/**
 * A call described by an ERC-7730 descriptor (ZKX clear signing): the intent ("Swap") on the
 * first line, then one "label: value" row per field the descriptor shows (amounts with their
 * token, recipient...). Values reuse the regular humanizer parts, so tokens and addresses look and
 * behave exactly like everywhere else.
 */
const Erc7730Visualization: FC<Props> = ({ visualization, PartsRenderer, ...partProps }) => {
  const { i18n } = useTranslation()
  const excluded = new Set(visualization.excludedFieldPaths)
  const rows = visualization.fields.filter((row) => !row.path || !excluded.has(row.path))
  const labelSize = Math.max(partProps.textSize - 2, 10)
  const getRowLabel = (label: string) =>
    translateErc7730FieldLabel(
      label,
      isKnownErc7730FieldLabel(label),
      { language: i18n.language, getResource: (lng, ns, key) => i18n.getResource(lng, ns, key) },
      I18N_NAMESPACE
    )

  return (
    <View style={{ width: '100%' }} testID="erc7730-visualization">
      <PartsRenderer data={visualization.intent} hasPadding={false} {...partProps} />
      {rows.map((row, index) => (
        <View
          // Rows have no id of their own; their order is fixed by the descriptor
          // eslint-disable-next-line react/no-array-index-key
          key={`${row.path || 'row'}-${index}`}
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            flexbox.wrap,
            { marginTop: index === 0 ? SPACING_TY : SPACING_MI }
          ]}
          testID={`erc7730-row-${index}`}
        >
          {row.type === 'single-value' && (
            <Text fontSize={labelSize} appearance="secondaryText" style={{ marginRight: SPACING_TY }}>
              {getRowLabel(row.label)}
            </Text>
          )}
          <View style={flexbox.flex1}>
            <PartsRenderer
              data={row.type === 'single-value' ? [row.value] : row.value}
              hasPadding={false}
              {...partProps}
            />
          </View>
        </View>
      ))}
    </View>
  )
}

export default memo(Erc7730Visualization)
